import React, { useEffect, useState } from "react";
import axios from "axios";
import { Lock, RefreshCw, LogOut, ShoppingCart, CreditCard, Mail, Users, DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE = "flowlux_admin_auth";

const money = (n) => `$${(n || 0).toFixed(2)}`;
const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return s || "";
  }
};

const Login = ({ onAuth }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const token = btoa(`${u}:${p}`);
    try {
      await axios.get(`${API}/admin/verify`, { headers: { Authorization: `Basic ${token}` } });
      sessionStorage.setItem(STORAGE, token);
      onAuth(token);
    } catch {
      setErr("Invalid username or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A] px-5">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#F4EFE8] rounded-lg p-8">
        <div className="flex items-center gap-2 mb-6">
          <Lock size={18} />
          <span className="font-serif-display text-2xl font-bold tracking-[0.2em]">FLOWLUX</span>
        </div>
        <h1 className="text-[13px] tracking-[0.2em] uppercase mb-6 text-[#1A1A1A]/60">Admin Dashboard</h1>
        <input value={u} onChange={(e) => setU(e.target.value)} placeholder="Username" className="w-full border border-[#1A1A1A]/25 rounded-sm px-4 py-3 text-[14px] mb-3 bg-white outline-none focus:border-[#1A1A1A]" />
        <input value={p} onChange={(e) => setP(e.target.value)} type="password" placeholder="Password" className="w-full border border-[#1A1A1A]/25 rounded-sm px-4 py-3 text-[14px] mb-4 bg-white outline-none focus:border-[#1A1A1A]" />
        {err && <p className="text-[13px] text-[#b91c1c] mb-4">{err}</p>}
        <button disabled={loading} className="btn-lux w-full py-3.5 text-[12px] tracking-[0.22em] uppercase disabled:opacity-60">
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-lg border border-[#1A1A1A]/10 p-5">
    <div className="flex items-center gap-2 text-[#6D28D9] mb-2"><Icon size={18} /></div>
    <p className="font-serif-display text-[32px] leading-none font-medium">{value}</p>
    <p className="text-[11px] tracking-[0.14em] uppercase text-[#1A1A1A]/50 mt-2">{label}</p>
  </div>
);

const Table = ({ columns, rows, empty }) => (
  <div className="overflow-x-auto rounded-lg border border-[#1A1A1A]/10 bg-white">
    <table className="w-full text-[13px]">
      <thead>
        <tr className="bg-[#EDE6DC] text-left">
          {columns.map((c) => (
            <th key={c.key} className="px-4 py-3 font-medium tracking-wide text-[#1A1A1A]/70 whitespace-nowrap">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-[#1A1A1A]/40">{empty}</td></tr>
        ) : (
          rows.map((r, i) => (
            <tr key={i} className="border-t border-[#1A1A1A]/8 hover:bg-[#F4EFE8]/60">
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 align-top whitespace-nowrap">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(STORAGE) || "");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("orders");
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/data`, { headers: { Authorization: `Basic ${t}` } });
      setData(res.data);
    } catch {
      sessionStorage.removeItem(STORAGE);
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) return <Login onAuth={setToken} />;

  const logout = () => {
    sessionStorage.removeItem(STORAGE);
    setToken("");
  };

  const c = data?.counts || {};
  const itemsSummary = (r) => (r.items || []).map((i) => `${i.title?.split("–")[0]?.trim() || "Item"} ×${i.qty}`).join(", ");

  const tabs = [
    { key: "orders", label: `Paid Orders (${(data?.orders || []).length})` },
    { key: "checkouts", label: `Checkouts (${(data?.checkouts || []).length})` },
    { key: "contacts", label: `Contact Forms (${(data?.contacts || []).length})` },
    { key: "subscribers", label: `Subscribers (${(data?.subscribers || []).length})` },
  ];

  return (
    <div className="min-h-screen bg-[#F4EFE8]">
      <header className="bg-[#1A1A1A] text-[#F4EFE8]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <span className="font-serif-display text-xl font-bold tracking-[0.2em]">FLOWLUX <span className="text-[11px] tracking-[0.2em] font-sans opacity-60">ADMIN</span></span>
          <div className="flex items-center gap-3">
            <button onClick={() => load(token)} className="flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase opacity-80 hover:opacity-100">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} className="flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase opacity-80 hover:opacity-100">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-5 md:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={DollarSign} label="Revenue (paid)" value={money(c.revenue)} />
          <StatCard icon={CreditCard} label="Paid Orders" value={c.paid_orders ?? 0} />
          <StatCard icon={ShoppingCart} label="Checkouts" value={c.checkouts ?? 0} />
          <StatCard icon={Mail} label="Contact Forms" value={c.contacts ?? 0} />
          <StatCard icon={Users} label="Subscribers" value={c.subscribers ?? 0} />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-5">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 text-[12px] tracking-[0.12em] uppercase rounded-full border transition-colors ${tab === t.key ? "bg-[#1A1A1A] text-[#F4EFE8] border-[#1A1A1A]" : "border-[#1A1A1A]/25 hover:border-[#1A1A1A]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <Table
            empty="No paid orders yet."
            columns={[
              { key: "order_number", label: "Order #" },
              { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
              { key: "name", label: "Customer", render: (r) => `${r.first_name || ""} ${r.last_name || ""}` },
              { key: "email", label: "Email" },
              { key: "items", label: "Items", render: itemsSummary },
              { key: "total", label: "Total", render: (r) => money(r.total) },
              { key: "address", label: "Ship To", render: (r) => `${r.address || ""}, ${r.city || ""}, ${r.state || ""} ${r.zip_code || ""}` },
              { key: "phone", label: "Phone" },
              { key: "status", label: "Status" },
            ]}
            rows={data?.orders || []}
          />
        )}

        {tab === "checkouts" && (
          <Table
            empty="No checkouts started yet."
            columns={[
              { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
              { key: "items", label: "Items", render: itemsSummary },
              { key: "subtotal", label: "Subtotal", render: (r) => money(r.subtotal) },
              { key: "email", label: "Email" },
              { key: "status", label: "Status" },
            ]}
            rows={data?.checkouts || []}
          />
        )}

        {tab === "contacts" && (
          <Table
            empty="No contact submissions yet."
            columns={[
              { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "phone", label: "Phone" },
              { key: "order_number", label: "Order #" },
              { key: "subject", label: "Subject" },
              { key: "message", label: "Message", render: (r) => <span className="whitespace-normal block max-w-md">{r.message}</span> },
            ]}
            rows={data?.contacts || []}
          />
        )}

        {tab === "subscribers" && (
          <Table
            empty="No subscribers yet."
            columns={[
              { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
              { key: "email", label: "Email" },
              { key: "source", label: "Source" },
            ]}
            rows={data?.subscribers || []}
          />
        )}
      </main>
    </div>
  );
};

export default Admin;
